import { useState, useEffect, useCallback } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import StepWizard from 'react-step-wizard'; // { StepWizardChildProps }
import { PolicyStreamPageContainer } from './components/PageContainer';
import { useSelector } from 'react-redux';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import {
  generateGetAnsweredForm,
  getSelectedPlan,
} from '../../store/policystreamStore';
import { useEnsureFormPresent } from '../../hooks/useEnsureFormPresent';
import { PolicystreamForm } from './components/PolicystreamForm';
import { policyStreamPaths } from './policyStreamConstants';
import LoadingScreen from '../ManagementPanel/components/LoadingScreen';
// import { useSubmitPolicyStreamQuote } from '../../hooks';
import { PolicyStreamLayout } from './components';
import { FormApi, FormState } from 'informed';
import { POLICYSTREAM_FORMS, POLICYSTREAM_API_NAMES } from './constants';
import Bugsnag from '@bugsnag/browser';
import { errorify } from '@calefy-inc/utility';
import { safelyGetAttributes } from '../../util';
import { getPolicyStreamInstitutionNumber } from './utility';
import { useSetStepStatus } from '../../hooks';
import { isEmptyValue } from './isEmptyValue';
import {
  MissingInformation,
  useMissingClientInfoGuard,
  useMissingPlanSelectionGuard,
} from './components/MissingInformation';
import { $TSFixMe } from '@calefy-inc/utilityTypes';
import { FormValues, InitialValues } from '../../Typescript/forms';

export const PolicyStreamPlanSpecificQuestionsPage = (
  _props: RouteComponentProps,
) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  const [formInternalName, setFormInternalName] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<
    undefined | InitialValues | null
  >();

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.PlanSpecificQuestions,
      EventActions.PageLoad,
      'PolicyStream Plan Specific Questions Page',
    );
  }, [myAnalytics]);
  const [missing, setMissing] = useState<
    undefined | { missing: string; url: string }
  >();
  const selectedPlan = useSelector(getSelectedPlan);
  const setStepStatus = useSetStepStatus('Application Form');
  const basicInfoAnswers = useSelector(
    generateGetAnsweredForm({ internalName: POLICYSTREAM_FORMS.basicInfo }),
  );
  const basicClientInfoAnswers = useSelector(
    generateGetAnsweredForm({
      internalName: POLICYSTREAM_FORMS.basicClientInfo,
    }),
  );
  const form = useEnsureFormPresent(
    formInternalName ? { internalName: formInternalName } : null,
  );
  const navigate = useNavigate();
  // const submitQuote = useSubmitPolicyStreamQuote();

  const missingBasicClientInfo = useMissingClientInfoGuard();
  const missingPlanSelection = useMissingPlanSelectionGuard();

  useEffect(() => {
    setStepStatus('IN_PROGRESS');
  }, []);

  // get the form name from the plan
  useEffect(() => {
    // in the future, get different forms based on the provider
    if (selectedPlan) {
      setFormInternalName(POLICYSTREAM_FORMS.applicationForm);
    } else {
      setFormInternalName(POLICYSTREAM_FORMS.applicationForm);
    }
  }, [selectedPlan]);

  // once we have the form, try to set the initial values from the earlier forms
  useEffect(() => {
    if (!form) {
      return;
    }
    // for now we only know how to convert to the general application form
    if (
      // @ts-expect-error
      ![POLICYSTREAM_FORMS.applicationForm].includes(form.internalName)
    ) {
      setInitialValues(null);
      return;
    }
    const personalInformation: $TSFixMe = {};
    if (basicInfoAnswers) {
      [
        POLICYSTREAM_API_NAMES.basicInfo.dateOfBirth,
        POLICYSTREAM_API_NAMES.basicInfo.sex,
      ].forEach(
        (apiName) =>
          (personalInformation[apiName] =
            basicInfoAnswers.getAnswerValueByApiName(apiName)),
      );
    }
    if (basicClientInfoAnswers) {
      // NB This is so dangerous - relies on the API names being the same between the forms. Change so that it takes the api names in the basic client info and application forms separately and does the mapping.
      [
        POLICYSTREAM_API_NAMES.basicClientInfo.firstName,
        POLICYSTREAM_API_NAMES.basicClientInfo.lastName,
        POLICYSTREAM_API_NAMES.basicClientInfo.email,
        POLICYSTREAM_API_NAMES.basicClientInfo.phoneNumber,
      ].forEach(
        (apiName) =>
          (personalInformation[apiName] =
            basicClientInfoAnswers.getAnswerValueByApiName(apiName)),
      );
    }
    const hasPrefillValues = Object.values(personalInformation).some(
      (value) => !isEmptyValue(value),
    );
    setInitialValues(
      hasPrefillValues
        ? {
            [POLICYSTREAM_API_NAMES.applicationGeneral.personalInformation]: [
              personalInformation,
            ],
          }
        : null,
    );
  }, [form, basicInfoAnswers, basicClientInfoAnswers]);

  const validateForm = useCallback(
    (values: FormValues) => {
      if (form?.internalName !== POLICYSTREAM_FORMS.applicationForm) {
        return undefined;
      }
      // check that the sum for the primary beneficiaries is 100%
      if (
        POLICYSTREAM_API_NAMES.applicationGeneral.beneficiaryInformation in
        values
      ) {
        let beneficiaryInformation;
        const beneficiaryInformationList =
          values[
            POLICYSTREAM_API_NAMES.applicationGeneral.beneficiaryInformation
          ];
        if (
          Array.isArray(beneficiaryInformationList) &&
          beneficiaryInformationList.length > 0
        ) {
          beneficiaryInformation = beneficiaryInformationList[0];
        } else {
          return;
        }
        //console.log('beneficiary information:', beneficiaryInformation);
        const primaryBeneficiariesRaw =
          beneficiaryInformation[
            POLICYSTREAM_API_NAMES.applicationGeneral.primaryBeneficiaries
          ];
        // Filter out empty beneficiaries
        const primaryBeneficiaries = Array.isArray(primaryBeneficiariesRaw)
          ? primaryBeneficiariesRaw.filter(
              (beneficiary) => !isEmptyValue(beneficiary),
            )
          : primaryBeneficiariesRaw;
        //console.log('primaryBeneficiaries:', primaryBeneficiaries);
        const contingentConditionalList =
          beneficiaryInformation[
            POLICYSTREAM_API_NAMES.applicationGeneral
              .contingentBeneficiariesConditional
          ];
        //console.log('ocntingentConditionalList:', contingentConditionalList);
        const contingentConditional =
          contingentConditionalList &&
          Array.isArray(contingentConditionalList) &&
          contingentConditionalList.length > 0
            ? contingentConditionalList[0]
            : undefined;
        //console.log('contingentConditional', contingentConditional);

        const contingentBeneficiariesRaw = contingentConditional
          ? contingentConditional[
              POLICYSTREAM_API_NAMES.applicationGeneral.contingentBeneficiaries
            ]
          : [];
        // Filter out empty beneficiaries
        const contingentBeneficiaries = Array.isArray(
          contingentBeneficiariesRaw,
        )
          ? contingentBeneficiariesRaw.filter(
              (beneficiary) => !isEmptyValue(beneficiary),
            )
          : contingentBeneficiariesRaw;
        //console.log('contingentBeneficiaries:', contingentBeneficiaries);

        for (let [beneficiaries, beneficiariesType] of [
          [primaryBeneficiaries, 'primary'],
          [contingentBeneficiaries, 'contingent'],
        ]) {
          //console.log(`Processing `, beneficiaries, beneficiariesType);
          if (!beneficiaries || beneficiaries.length === 0) {
            continue;
          }
          const shareSum = beneficiaries.reduce(
            (sum: number, beneficiary: object) => {
              try {
                //console.log({ sum, beneficiary });
                const shareRaw =
                  // @ts-expect-error
                  beneficiary[
                    POLICYSTREAM_API_NAMES.applicationGeneral.beneficiaryShare
                  ];
                //console.log({ shareRaw });
                const individualShare = shareRaw
                  ? Number(shareRaw.replace(/\%/g, ''))
                  : 0;
                if (
                  !Number.isNaN(individualShare) &&
                  Number.isFinite(individualShare)
                ) {
                  return sum + individualShare;
                } else {
                  throw new Error(
                    `Error: unable to parse beneficiary share from ${JSON.stringify(
                      beneficiary,
                      null,
                      4,
                    )}`,
                  );
                }
              } catch (e) {
                console.error(
                  `Error when summing shares for ${beneficiariesType} beneficiaries:`,
                  e,
                );
                Bugsnag.notify(errorify(e));
                return sum;
              }
            },
            0,
          );
          if (shareSum !== 100) {
            return `Error: the sum of shares for ${beneficiariesType} beneficiaries must equal 100%; it is currently at ${parseFloat(
              shareSum.toFixed(3),
            )}%. Please adjust the amounts.`;
          }
        }
        return undefined;
      }
    },
    [form],
  );

  const generateOnFormChange = useCallback(
    (formApi: FormApi) => {
      return (formState: FormState) => {
        try {
          if (form?.internalName !== POLICYSTREAM_FORMS.applicationForm) {
            return;
          }
          const { values } = formState;
          const bankName = safelyGetAttributes(values, [
            POLICYSTREAM_API_NAMES.applicationGeneral.bankingInformation,
            0,
            POLICYSTREAM_API_NAMES.applicationGeneral.bankNameConditional,
            0,
            POLICYSTREAM_API_NAMES.applicationGeneral.bankName,
          ]);
          if (!bankName) {
            return;
          }
          if (!(typeof bankName === 'string')) {
            return;
          }
          const institutionNumber = getPolicyStreamInstitutionNumber(bankName);
          const existingInstitutionNumber = safelyGetAttributes(values, [
            POLICYSTREAM_API_NAMES.applicationGeneral.bankingInformation,
            0,
            POLICYSTREAM_API_NAMES.applicationGeneral.institutionNumber,
          ]);
          if (
            institutionNumber &&
            institutionNumber !== existingInstitutionNumber
          ) {
            formApi.setValue(
              `${POLICYSTREAM_API_NAMES.applicationGeneral.bankingInformation}[0].${POLICYSTREAM_API_NAMES.applicationGeneral.institutionNumber}`,
              institutionNumber,
            );
          }
        } catch (e) {
          console.error(e);
          Bugsnag.notify(errorify(e));
        }
      };
    },
    [form],
  );

  // set the missing information if we are in fact missing something
  useEffect(() => {
    let missingString = '';
    let missingUrl = '';
    if (missingBasicClientInfo) {
      missingString = missingBasicClientInfo.missing;
      missingUrl = policyStreamPaths.postPlanSelectionInformation.url;
    } else if (missingPlanSelection) {
      missingString = missingPlanSelection.missing;
      missingUrl = policyStreamPaths.planSelection.url;
    }

    setMissing(
      missingString && missingUrl
        ? { missing: missingString, url: missingUrl }
        : undefined,
    );
  }, [missingBasicClientInfo, missingPlanSelection]);

  return (
    <PolicyStreamLayout>
      {missing ? (
        <MissingInformation
          missing={missing.missing}
          redirectURL={missing.url}
          stepName='Application Form'
        />
      ) : (
        <PolicyStreamPageContainer>
          <h2>Application</h2>
          {form && initialValues !== undefined ? (
            <StepWizard isLazyMount>
              {(form.questionInstances || []).map((question) => (
                <PolicystreamForm
                  validateForm={validateForm}
                  generateOnFormChange={generateOnFormChange}
                  initialValues={initialValues}
                  onFirstPrev={() => {
                    navigate(policyStreamPaths.planSelection.url);
                  }}
                  onLastNext={() => {
                    // submitQuote();
                    navigate(policyStreamPaths.review.url);
                    setStepStatus('COMPLETED');
                  }}
                  form={form.copyWithAmendments({
                    questionInstances: [question],
                  })}
                />
              ))}
            </StepWizard>
          ) : (
            <LoadingScreen />
          )}
        </PolicyStreamPageContainer>
      )}
    </PolicyStreamLayout>
  );
};
